import {
  type Dispatch,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from 'react';
import type { StoreApi } from 'zustand';

import type { CalendarProps } from './Calendar.types.js';
import { CALENDAR_ERROR_MESSAGES } from './calendarMessages.js';
import { CalendarRoot } from './CalendarRoot.js';
import type { CalendarType } from './types/calendarConfiguration.js';
import {
  convertToCalendarStructure,
  getTimeOffsetFromInitialValue,
} from './utils/helpers.js';
import { useCallbackRef } from '../../core/hooks/useCallbackRef.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { isFullTimeframe } from '../../core/time/helpers.js';
import type { Precision } from '../../core/time/shared.types.js';
import { getCalendarActions } from '../../core/time/timeframeStore/actions/calendar.js';
import type {
  CalendarTimeframeState,
  CalendarTimeframeStoreActions,
} from '../../core/time/timeframeStore/calendar.types.js';
import { createTimeframeStore } from '../../core/time/timeframeStore/createTimeframeStore.js';
import { CalendarStoreProvider } from '../../core/time/timeframeStore/timeframeStoreProvider.js';
import { useMinMax, useMinMaxStoreSync } from '../../core/time/useMinMax.js';
import { validate } from '../../core/time/validation.js';
import { type TimeValue } from '../../core/types/time.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import { useMinMaxLogger } from '../forms-core/hooks/useMinMaxLogger.js';
import type { FormFieldMessagesReducerAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type { FormControlRef, TimeRangePickerRef } from '../shared-types.js';

type CalendarStore = StoreApi<
  CalendarTimeframeState & CalendarTimeframeStoreActions
>;

/**
 * Extracts the prop-to-store syncing effects into a dedicated hook so the
 * component body stays focused on wiring rather than implementation details.
 *
 * According to the zustand maintainer, the recommended way to keep a zustand
 * store in sync with React props is via useEffect.
 * see https://github.com/pmndrs/zustand/discussions/1390#discussion-4505803
 */
function useSyncPropsAndState(
  store: CalendarStore,
  memoedValue: CalendarTimeframeState['calendarValue'],
  isControlled: boolean,
  type: CalendarType,
  precision: Exclude<Precision, 'day'>,
  min: TimeValue | null,
  max: TimeValue | null,
  formFieldMessageDispatch:
    | Dispatch<FormFieldMessagesReducerAction>
    | undefined,
  calendarRef: CalendarTimeframeState['calendarRef'],
) {
  const valueMode = type === 'single' ? 'single' : 'timeframeSelector';
  // Sync the controlled value into the store whenever it changes.
  useEffect(
    () => {
      isControlled &&
        store.setState((state) => {
          const error = validate(memoedValue, state.min, state.max, {
            formMessageDispatch: formFieldMessageDispatch,
            formerError: state.calendarError,
            precision: state.precision,
            showError: false,
            checkOrder: true,
            ref: calendarRef,
            valueMode,
            errorMessages: CALENDAR_ERROR_MESSAGES,
          });

          const timeOffset = getTimeOffsetFromInitialValue(
            memoedValue,
            type,
            min,
            max,
            precision,
          );

          const valueWithPrecision = convertToCalendarStructure(
            type,
            memoedValue,
            min,
            max,
            precision,
          );

          if (type === 'single') {
            return {
              calendarValue: valueWithPrecision,
              calendarError: error,
              timeOffset,
            };
          }

          const value = isFullTimeframe(valueWithPrecision)
            ? valueWithPrecision
            : null;

          return {
            calendarValue: value,
            calendarError: error,
            timeOffset,
          };
        });
    },
    // calendarRef is a stable ref — no need to include it in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formFieldMessageDispatch,
      isControlled,
      memoedValue,
      store,
      min,
      max,
      precision,
      type,
    ],
  );

  // Sync precision, type, min, and max into the store whenever they change.
  useEffect(() => {
    store.setState((state) => {
      const value = convertToCalendarStructure(
        type,
        state.calendarValue,
        min,
        max,
        precision,
      );

      return {
        ...state,
        precision,
        calendarValue: value,
      };
    });
  }, [precision, store, min, max, type]);
}

function RefAwareCalendar<Type extends CalendarType = 'single'>(
  props: CalendarProps<Type>,
  forwardedRef: Ref<
    | FormControlRef<HTMLDivElement>
    | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
        Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>)
  >,
) {
  const {
    value: propValue,
    defaultValue: propDefaultValue,
    onChange,
    min: propMin,
    max: propMax,
    disabled: propDisabled,
    required: propRequired,
    readOnly: propReadOnly,
    id: propId,
    'aria-disabled': propAriaDisabled,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- APPDEV-18321
    controlState,
    precision = 'minutes',
    type = 'single',
    ...remainingProps
  } = props;

  const [isControlled] = useState(propValue !== undefined);
  const onChangeProp = useCallbackRef(onChange);

  // Keep min and max updated at midnight
  const { min, max } = useMinMax(propMin, propMax);
  useMinMaxLogger(min, max, 'Calendar');

  const memoedValue = useMemo(() => {
    return convertToCalendarStructure(type, propValue, min, max, precision);
  }, [precision, propValue, min, max, type]);
  const memoedDefaultValue = useMemo(
    () =>
      convertToCalendarStructure(type, propDefaultValue, min, max, precision),
    /** We're ignoring updates to defaultValue as it only considers the initial state. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const calendarRef = useRef<
    | FormControlRef<HTMLDivElement>
    | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
        Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>)
  >(null);

  const {
    disabled,
    required,
    id,
    ariaDisabled,
    readOnly,
    messageDispatch: formFieldMessageDispatch,
  } = useFormControl({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled,
    propReadOnly,
    controlState,
  });

  const [store] = useState(() => {
    const value = isControlled ? memoedValue : memoedDefaultValue;

    const error = validate(value, min, max, {
      formMessageDispatch: formFieldMessageDispatch,
      precision,
      showError: false,
      checkOrder: true,
      ref: calendarRef,
      valueMode: type === 'single' ? 'single' : 'timeframeSelector',
      required: required,
      errorMessages: CALENDAR_ERROR_MESSAGES,
    });
    const timeOffset = getTimeOffsetFromInitialValue(
      value,
      type,
      min,
      max,
      precision,
    );

    const initialState = {
      calendarValue: value,
      min,
      max,
      precision,
      isFullDay: false,
      calendarRef,
      required,
      calendarError: error,
      timeOffset,
    };

    const actions = getCalendarActions<Type>(
      isControlled,
      type,
      formFieldMessageDispatch,
      onChangeProp,
    );
    return createTimeframeStore<
      CalendarTimeframeState,
      CalendarTimeframeStoreActions
    >(initialState, actions);
  });

  useSyncPropsAndState(
    store,
    memoedValue,
    isControlled,
    type,
    precision,
    min,
    max,
    formFieldMessageDispatch,
    calendarRef,
  );

  // Keep min and max updated at midnight
  useMinMaxStoreSync(
    store,
    min,
    max,
    useCallback(
      (
        state: CalendarTimeframeState,
        min: TimeValue | null,
        max: TimeValue | null,
      ) => ({
        calendarError: validate(state.calendarValue, min, max, {
          formMessageDispatch: formFieldMessageDispatch,
          precision,
          showError: false,
          checkOrder: true,
          ref: calendarRef,
          valueMode: type === 'single' ? 'single' : 'timeframeSelector',
          required: required,
          errorMessages: CALENDAR_ERROR_MESSAGES,
        }),
      }),
      [formFieldMessageDispatch],
    ),
  );

  const mergedRef = useMergeRefs([calendarRef, forwardedRef]);

  return (
    <CalendarStoreProvider store={store}>
      <CalendarRoot
        {...remainingProps}
        id={id}
        ref={mergedRef}
        aria-disabled={ariaDisabled}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        type={type}
      />
    </CalendarStoreProvider>
  );
}

/**
 * Calendar component
 * Renders the calendar header and body depending on the current view
 * @internal
 */
export const Calendar = forwardRef(RefAwareCalendar);
